import { Alert, AlertTitle, Button } from "@mui/material"
import React, { useContext, useEffect, useSyncExternalStore } from "react"
import { InfoContext } from "@app/components/layout/InfoSnackbar"
import { useFrontUpdate } from "@app/core/hooks/use-front-update"
import { frontUpdate, frontUpdateNoticeMessage } from "@app/core/services/front-update"

/**
 * Keeps the page on the deployed version of the front (front-update.ts). Shown only while the page cannot reload by
 * itself without losing what the user typed: never an error, the page stays usable to copy what is on it.
 */
export function FrontUpdateBanner(): React.JSX.Element | null {
  useFrontUpdate()
  const openInfoSnackbar = useContext(InfoContext)
  const { visible, lastChangeNotSaved } = useSyncExternalStore(frontUpdate.subscribe, frontUpdate.bannerState)

  useEffect(() => {
    const notice = frontUpdate.takeNoticeOfTheReload()
    if (notice) {
      openInfoSnackbar(frontUpdateNoticeMessage(notice, import.meta.env.VITE_APP_VERSION))
    }
  }, [openInfoSnackbar])

  if (!visible) {
    return null
  }

  return (
    <Alert
      severity="info"
      role="status"
      action={
        <Button color="inherit" size="small" onClick={() => frontUpdate.reloadNow()}>
          Recharger
        </Button>
      }
      sx={{
        position: "fixed",
        bottom: 16,
        left: "50%",
        transform: "translateX(-50%)",
        width: "min(560px, calc(100% - 32px))",
        boxShadow: 3,
        zIndex: (theme) => theme.zIndex.snackbar,
      }}>
      <AlertTitle>Time To Beem a été mis à jour</AlertTitle>
      {lastChangeNotSaved
        ? "Votre dernière modification n'a pas été enregistrée. Copiez ce que vous avez saisi, puis rechargez la page pour continuer."
        : "Rechargez la page pour continuer. Cette page ne peut plus rien enregistrer : copiez d'abord ce que vous avez saisi sans l'enregistrer."}
    </Alert>
  )
}
